import { Bell, Sun, Moon, User, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@hooks/ui/useTheme';
import { useAuth, useCurrentUser } from '@hooks/auth';
// Language switcher hidden from the topbar — browser-detected language +
// language packs still work via i18next's LanguageDetector. Restore by
// uncommenting this import and the <LanguageSwitcher /> render below.
// import { LanguageSwitcher } from '@components/composition/LanguageSwitcher';
import { OrderSearchBar } from '@components/composition/OrderSearchBar';
import { OrderMeshLogo } from '@components/composition/OrderMeshLogo';
import { useHasNavAccess } from './useHasNavAccess';

import type { JSX } from "react";

export function Topbar(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark, toggle } = useTheme();
  const user = useCurrentUser();
  const { signOut } = useAuth();
  const hasNavAccess = useHasNavAccess();

  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : '';

  return (
    <header
      className="flex h-14 items-center justify-between border-b bg-background px-4"
      data-testid="topbar"
    >
      <button
        type="button"
        className="flex items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => navigate('/home')}
        aria-label={t('navigation:topbar.home', { defaultValue: 'Home' })}
        data-testid="topbar-brand"
      >
        <OrderMeshLogo
          className="h-3.5 w-auto text-foreground"
          data-testid="topbar-logo"
          role="img"
        />
        <span className="text-sm text-muted-foreground">{t('navigation:brand.suffix')}</span>
      </button>

      {hasNavAccess && (
        <div className="flex max-w-md flex-1 items-center gap-2 px-8">
          <OrderSearchBar />
        </div>
      )}

      <div className="flex items-center gap-1">
        {/* <LanguageSwitcher /> */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          aria-label={isDark ? t('common:theme.switchToLight') : t('common:theme.switchToDark')}
          data-testid="topbar-theme-toggle"
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('navigation:topbar.notifications')}
          data-testid="topbar-notifications"
        >
          <Bell className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-10 gap-2 px-2"
              aria-label={t('navigation:topbar.userMenu')}
              data-testid="topbar-profile-menu"
            >
              <User className="h-4 w-4" />
              {displayName && (
                <span
                  className="max-w-[12rem] truncate text-sm font-medium text-foreground"
                  data-testid="topbar-profile-name"
                >
                  {displayName}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[16rem]">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t('auth:userMenu.signedInAs')}
                </span>
                <span className="text-sm font-medium normal-case tracking-normal text-foreground">
                  {displayName}
                </span>
                {user?.email && displayName !== user.email && (
                  <span className="text-xs font-normal normal-case tracking-normal text-muted-foreground">
                    {user.email}
                  </span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={false}
              onSelect={() => {
                // Dedicated own-profile route, accessible without users:search
                // (the /admin/users/* tree is permission-gated). See app/routes.tsx.
                navigate('/profile');
              }}
              data-testid="topbar-profile-menu-option-userprofile"
            >
              <User className="mr-2 h-4 w-4" />
              {t('auth:userMenu.profile')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={false}
              onSelect={() => {
                void signOut();
              }}
              data-testid="topbar-profile-menu-option-logout"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth:session.signOut')}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
